import { Card, Input, Table } from 'antd';
import numeral from 'numeral';
import pluralize from 'pluralize';
import React from 'react';

import { CsvExportButton } from '../../../../components/CsvExport/CsvExportButton';
import { VirtualDirectoryUnitFragment } from '../../../../graphql/api/generated';
import type { Refetch } from '../../../../utils';
import { normalizeColumns } from '../../../helpers/normalizeColumns';

import { createUnitActionsColumn, createUnlistedReasonsColumn, unitColumns } from './columns';
import { useVirtualDirectoryUnitFilters } from './hooks/useVirtualDirectoryUnitFilters';

interface IVirtualDirectoryUnitsProps {
  units: VirtualDirectoryUnitFragment[];
  unlistedReasonFilters: string[];
  canViewResidents: boolean;
  csvExportTitle: string;
  loading: boolean;
  refetch: Refetch;
}

export const VirtualDirectoryUnits: React.FC<IVirtualDirectoryUnitsProps> = (props) => {
  const {
    units: allUnits, unlistedReasonFilters, canViewResidents, csvExportTitle, loading, refetch,
  } = props;

  const {
    setFilters,
    onSearch,
    filteredUnits,
  } = useVirtualDirectoryUnitFilters(allUnits);

  let columns = [
    ...unitColumns,
    createUnlistedReasonsColumn(unlistedReasonFilters),
  ];

  if (!canViewResidents) {
    columns = columns.filter(c => c.title !== 'Residents');
  }

  const normalizedColumns = normalizeColumns(columns);

  const displayedTotal = (
    `${numeral(filteredUnits.length).format('1,000')} ` +
    `${pluralize('unit', filteredUnits.length)}`
  );

  return (
    <Card
      title={displayedTotal}
      bordered={false}
      bodyStyle={{ margin: 0, padding: 0 }}
      headStyle={{ margin: 0, padding: 0, borderBottom: 'none' }}
      extra={(
        <div style={{ display: 'flex' }}>
          <CsvExportButton
            title={csvExportTitle}
            rows={filteredUnits}
            columns={columns}
            disabled={loading}
            buttonStyle={{ marginRight: 8 }}
          />
          <Input.Search
            enterButton
            allowClear
            placeholder={canViewResidents
              ? 'Search by unit number or resident name'
              : 'Search by unit number'
            }
            onChange={e => onSearch(e.target.value)}
            onSearch={value => onSearch(value)}
            style={{ minWidth: 350 }}
          />
        </div>
      )}
    >
      <Table
        loading={{ size: 'large', spinning: loading }}
        rowKey={u => u.unitId}
        dataSource={filteredUnits}
        scroll={{ x: true }}
        columns={[
          ...normalizedColumns,
          createUnitActionsColumn(refetch),
        ]}
        showSorterTooltip={false}
        pagination={false}
        style={{ clear: 'both', marginTop: 20 }}
        onChange={(pagination, nextFilters) => setFilters(nextFilters)}
      />
    </Card>
  );
};
